import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const WalletContext = createContext(null);

// Robinhood Chain
export const NAV_CHAIN_ID = 4663;
export const NAV_CHAIN_HEX = "0x" + NAV_CHAIN_ID.toString(16); // 0x1237

const NAV_CHAIN_PARAMS = {
  chainId: NAV_CHAIN_HEX,
  chainName: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://rpc.robinhoodchain.example"], // preview placeholder
  blockExplorerUrls: ["https://explorer.robinhoodchain.example"],
};

const DEMO = {
  mode: "demo",
  address: "0x8a…4e21",
  chainId: NAV_CHAIN_ID,
  balances: { USDG: 12480.42, ETH: 1.842, NVDA: 12, SPY: 4 },
};

const short = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");

export function WalletProvider({ children }) {
  const [wallet, setWallet] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [switching, setSwitching] = useState(false);

  const eth = typeof window !== "undefined" ? window.ethereum : null;
  const hasMetaMask = !!eth;

  // ---------- MetaMask ----------
  const readChain = useCallback(async () => {
    if (!eth) return null;
    const hex = await eth.request({ method: "eth_chainId" });
    return parseInt(hex, 16);
  }, [eth]);

  const connectMetaMask = useCallback(async () => {
    setError(null);
    if (!eth) {
      setError("MetaMask not detected.");
      return;
    }
    try {
      const accounts = await eth.request({ method: "eth_requestAccounts" });
      const chainId = await readChain();
      setWallet({
        mode: "metamask",
        address: short(accounts[0]),
        rawAddress: accounts[0],
        chainId,
      });
      setOpen(false);
    } catch (e) {
      setError(e?.message || "Failed to connect");
    }
  }, [eth, readChain]);

  const switchChain = useCallback(async () => {
    if (!eth) return;
    setSwitching(true);
    setError(null);
    try {
      await eth.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: NAV_CHAIN_HEX }],
      });
    } catch (e) {
      // 4902 = chain not added
      if (e?.code === 4902 || /Unrecognized chain/i.test(e?.message || "")) {
        try {
          await eth.request({
            method: "wallet_addEthereumChain",
            params: [NAV_CHAIN_PARAMS],
          });
        } catch (err) {
          setError(err?.message || "Failed to add chain");
        }
      } else {
        setError(e?.message || "Failed to switch");
      }
    } finally {
      setSwitching(false);
    }
  }, [eth]);

  // Listen for account/chain changes
  useEffect(() => {
    if (!eth) return;
    const onAccounts = (accounts) => {
      if (!accounts?.length) {
        setWallet(null);
      } else {
        setWallet((w) =>
          w && w.mode === "metamask"
            ? { ...w, address: short(accounts[0]), rawAddress: accounts[0] }
            : w
        );
      }
    };
    const onChain = (hex) => {
      const cid = parseInt(hex, 16);
      setWallet((w) => (w && w.mode === "metamask" ? { ...w, chainId: cid } : w));
    };
    eth.on?.("accountsChanged", onAccounts);
    eth.on?.("chainChanged", onChain);
    return () => {
      eth.removeListener?.("accountsChanged", onAccounts);
      eth.removeListener?.("chainChanged", onChain);
    };
  }, [eth]);

  // ---------- Demo ----------
  const connectDemo = () => {
    setError(null);
    setWallet(DEMO);
    setOpen(false);
  };

  const disconnect = () => setWallet(null);

  const wrongChain = !!wallet && wallet.chainId !== NAV_CHAIN_ID;

  return (
    <WalletContext.Provider
      value={{
        wallet,
        open,
        setOpen,
        connectMetaMask,
        connectDemo,
        disconnect,
        hasMetaMask,
        switchChain,
        switching,
        wrongChain,
        error,
        NAV_CHAIN_ID,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export const useWallet = () => useContext(WalletContext);
